import type { WorkflowSchedule } from "api/workflowSchedules/models/WorkflowSchedule";
import { useMemo, useCallback, useEffect, useRef } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import { sendMessage } from "state/websocket/operations";
import { downloadWorkflowsSchedules } from "state/workflowSchedules/operations";
import type { RequestEntities } from "types/charliui";

export const useWorkflowSchedule = (scheduleId: string): WorkflowSchedule | undefined => {
  return useSelector((state: RootState) => state.workflowsSchedules.workflowsSchedulesById[scheduleId], shallowEqual);
};

export const useWorkflowSchedules = () => {
  return useSelector(
    (state: RootState) => state.workflowsSchedules.workflowsSchedulesIds.map((id) => state.workflowsSchedules.workflowsSchedulesById[id]),
    shallowEqual
  );
};

export const useRefreshWorkflowSchedules = () => {
  const dispatch = useDispatch();

  return useCallback(() => {
    // Use ref to track last refresh time
    const now = Date.now();
    if (!useRefreshWorkflowSchedules.lastRefresh || now - useRefreshWorkflowSchedules.lastRefresh > 5000) {
      useRefreshWorkflowSchedules.lastRefresh = now;
      return dispatch(downloadWorkflowsSchedules());
    }
    return Promise.resolve();
  }, [dispatch]);
};

// Add static property to track last refresh
useRefreshWorkflowSchedules.lastRefresh = 0;

export const useWorkflowSchedulesByPortfolio = (portfolioId: string) => {
  const workflowsSchedules = useWorkflowSchedules();
  const refreshWorkflowSchedules = useRefreshWorkflowSchedules();
  const hasInitialized = useRef(false);

  // Only refresh workflow schedules on first mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      refreshWorkflowSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () =>
      workflowsSchedules.filter((workflowSchedule) =>
        workflowSchedule.entities.find((entity) => entity.name === "portfolio_id" && entity.value === portfolioId)
      ),
    [workflowsSchedules, portfolioId]
  );
};

export const useWorkflowScheduleByProject = (collectionId: string) => {
  const workflowsSchedules = useWorkflowSchedules();

  return useMemo(
    () =>
      workflowsSchedules.find((workflowSchedule) =>
        workflowSchedule.entities.some((entity) => entity.name === "as_rerun_of_project_id" && entity.value === collectionId)
      ),
    [workflowsSchedules, collectionId]
  );
};

export const useCancelWorkflowSchedule = () => {
  const dispatch = useDispatch();
  const refreshWorkflowSchedules = useRefreshWorkflowSchedules();

  return useCallback(
    (workflowSchedule: WorkflowSchedule | undefined, collectionId: string, collectionConversationId: string | undefined) => {
      if (!workflowSchedule || !collectionId || !collectionConversationId) return;

      const entities: RequestEntities = [
        { entity: "workflow_schedule_id", value: workflowSchedule.id },
        { entity: "portfolio_id", value: collectionId },
      ];

      dispatch(
        sendMessage({
          conversationId: collectionConversationId,
          intent: "/delete_workflow_schedule",
          entities: entities,
        })
      );

      // Refresh workflow schedules immediately after sending the delete message
      refreshWorkflowSchedules();
    },
    [dispatch, refreshWorkflowSchedules]
  );
};

interface NewScheduledProject {
  scheduleId: string;
  portfolioId: string;
  companyTicker: string;
  companyStockExchange: string;
  companyName: string;
}

export const useScheduledPlaceholderProjectsByPortfolio = (portfolioId: string): NewScheduledProject[] => {
  const workflowsSchedules = useWorkflowSchedules();
  const hasLoadedRef = useRef(false);
  const refreshWorkflowSchedules = useRefreshWorkflowSchedules();
  const lastPortfolioIdRef = useRef(portfolioId);

  useEffect(() => {
    if (!hasLoadedRef.current || lastPortfolioIdRef.current !== portfolioId) {
      hasLoadedRef.current = true;
      lastPortfolioIdRef.current = portfolioId;
      const timeoutId = setTimeout(() => {
        refreshWorkflowSchedules();
      }, 1000); // Debounce for 1 second
      return () => clearTimeout(timeoutId);
    }
  }, [portfolioId, refreshWorkflowSchedules]);

  return useMemo(() => {
    return workflowsSchedules.flatMap((schedule) => {
      const entitiesMap = schedule.entities.reduce((acc: Map<string, string>, entity) => {
        acc.set(entity.name, entity.value);
        return acc;
      }, new Map<string, string>());

      if (
        schedule.frequency === "one-time" &&
        entitiesMap.get("portfolio_id") === portfolioId &&
        entitiesMap.has("company_ticker") &&
        entitiesMap.has("company_stock_exchange") &&
        entitiesMap.has("company_name")
      ) {
        return [
          {
            scheduleId: schedule.id,
            portfolioId,
            companyTicker: entitiesMap.get("company_ticker")!,
            companyStockExchange: entitiesMap.get("company_stock_exchange")!,
            companyName: entitiesMap.get("company_name")!,
          },
        ];
      } else {
        return [];
      }
    });
  }, [workflowsSchedules, portfolioId]);
};

export const useProjectsHaveWorkflowSchedules = (projectsIds: string[]) => {
  const workflowsSchedules = useWorkflowSchedules();

  return useMemo(
    () =>
      projectsIds.some((projectId) =>
        workflowsSchedules.some(
          (workflowSchedule) =>
            workflowSchedule.entities.some((entity) => entity.name === "as_rerun_of_project_id" && entity.value === projectId) &&
            workflowSchedule.frequency === "one-time"
        )
      ),
    [workflowsSchedules, projectsIds]
  );
};
